import React from 'react';

// The framing arithmetic for a normalized 0..1 crop, and the one component that
// applies it (#198, #234).
//
// Two unrelated tables now carry the same crop vocabulary, because the same
// photograph travels between them:
//
//   user_photo_dishes   a diner's visit photo, framed to one of several dishes
//                       it shows. Read via DishPhoto.
//   menu_item_images    that photo after the #117 bridge copies it to the public
//                       menu, carrying the crop across so a wide three-dish
//                       table shot does not stand in, uncropped, as the gallery
//                       picture for one dish. Read here and on the Food page.
//
// The maths lives here rather than in DishPhoto because #234 needed it on menu
// and Food surfaces too, and copying it would have made a third implementation
// of a thing that already has two consumers — precisely the shape of #67 (the
// restaurant card copy-pasted six times) and of #230, where #198's read path
// went into UserProfilePage.js while /profile renders UserProfile.js.
//
// Nothing here mutates the image. The crop is presentation metadata applied at
// render; the stored object is always the original, whole photo.

/**
 * The crop carried by a row that holds `crop_x/y/w/h` directly — a menu item
 * image, a Food page image, or one entry of a photo's `dishes` array.
 *
 * Returns null for "show the whole image", which is the correct answer for every
 * row predating #198 and every row no crop was drawn for.
 */
export function normalizeCrop(source) {
  if (!source) return null;
  const { crop_x: x, crop_y: y, crop_w: w, crop_h: h } = source;
  // The backend stores all four or none, and rejects a partial rectangle — but a
  // client must not render one either way: three of four fractions is an
  // unanswerable question, not a degraded crop.
  if ([x, y, w, h].some((v) => v == null)) return null;
  if (!(w > 0) || !(h > 0)) return null;
  return { x, y, w, h };
}

/**
 * Styles for the `<img>` inside a frame that is `position: relative` and
 * `overflow: hidden`.
 *
 * Blow the image up so the crop rectangle alone fills the frame, then slide it
 * so the crop's top-left sits at the frame's top-left. Percentages resolve
 * against the frame, so this holds at any frame size with no measurement and no
 * layout pass — width 1/w of the frame puts the crop's width exactly on the
 * frame's width, and the offset is the crop origin expressed at that scale.
 *
 * `cover` then absorbs any mismatch between the crop's aspect ratio and the
 * frame's. With the square frames used throughout and the fixed-proportion
 * square box the capture UI places, there is no mismatch and this is exact.
 */
export function croppedImageStyle(crop) {
  return {
    position: 'absolute',
    width: `${100 / crop.w}%`,
    height: `${100 / crop.h}%`,
    left: `${-100 * (crop.x / crop.w)}%`,
    top: `${-100 * (crop.y / crop.h)}%`,
    objectFit: 'cover',
  };
}

/**
 * An `<img>` that honours a crop when it has one.
 *
 * Uncropped, this renders the plain `<img>` the caller would have written by
 * hand — same tag, same class, same styles — so adding it to an existing render
 * site changes nothing until a crop actually arrives.
 *
 * Cropped, the caller's `className` moves to a frame element and the image is
 * scaled inside it. The class carries the intended BOX (width, height, radius,
 * border), which is exactly what the frame needs, so no dimension has to be
 * restated in JS where it could drift from the stylesheet.
 */
export default function CroppedImage({
  src,
  crop,
  alt = '',
  className,
  style,
  onClick,
  onError,
  title,
  children,
}) {
  const box = normalizeCrop(crop);

  if (!box) {
    return (
      <>
        <img
          src={src}
          alt={alt}
          className={className}
          style={style}
          onClick={onClick}
          onError={onError}
          title={title}
        />
        {children}
      </>
    );
  }

  return (
    <>
      <span
        className={className}
        style={{ ...style, display: 'block', position: 'relative', overflow: 'hidden' }}
        onClick={onClick}
        title={title}
      >
        <img src={src} alt={alt} style={croppedImageStyle(box)} onError={onError} />
      </span>
      {children}
    </>
  );
}
